import React, { useState } from 'react';
import { Modal, ModalDialog, ModalClose, DialogTitle, DialogContent, FormControl, FormLabel, Input, Button, Stack, Alert } from '@mui/joy';
import { useAdmin } from '../../contexts/Admin.tsx';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function ChangeAdminPasswordModal({ open, onClose }: Props) {
  const { api } = useAdmin();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const reset = () => {
    setOldPassword(''); setNewPassword(''); setConfirm('');
    setError(''); setSuccess(false);
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSuccess(false);
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setBusy(true);
    try {
      await api.changeMyPassword(oldPassword, newPassword);
      setSuccess(true);
      setOldPassword(''); setNewPassword(''); setConfirm('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ minWidth: 400 }}>
        <ModalClose />
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {error && <Alert color="danger" variant="soft">{error}</Alert>}
              {success && <Alert color="success" variant="soft">Password changed</Alert>}
              <FormControl required>
                <FormLabel>Current Password</FormLabel>
                <Input type="password" value={oldPassword} onChange={e => setOldPassword(e.target.value)} disabled={busy} />
              </FormControl>
              <FormControl required>
                <FormLabel>New Password</FormLabel>
                <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} disabled={busy} />
              </FormControl>
              <FormControl required>
                <FormLabel>Confirm New Password</FormLabel>
                <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} disabled={busy} />
              </FormControl>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="plain" color="neutral" onClick={handleClose} disabled={busy}>Cancel</Button>
                <Button type="submit" loading={busy}>Update</Button>
              </Stack>
            </Stack>
          </form>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
}
